import { useEffect } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import CO2Calculator from "@/components/CO2Calculator";
import { Link } from "react-router-dom";
import { applySeo } from "@/lib/seo";

export default function CalculatorCO2() {
  useEffect(() => {
    applySeo({
      title: "CO₂-Fußabdruck-Portfolio-Rechner – Grüne Geldanlage",
      description: "Portfolio-CO₂-Rechner: Schätzen Sie die Emissionsintensität Ihres Depots je nach Anteil klassischer, ESG-, Paris-Aligned- und Impact-Bausteine.",
      canonicalPath: "/rechner/co2-fussabdruck",
    });
  }, []);

  return (
    <div className="min-h-screen bg-brand-paper" data-testid="calc-co2-page">
      <Header />
      <section className="max-w-4xl mx-auto px-6 lg:px-10 pt-20 pb-10">
        <div className="overline mb-4">Rechner</div>
        <h1 className="font-serif text-5xl md:text-6xl font-light leading-tight tracking-tight text-brand-dark mb-6">
          CO₂-Fußabdruck-Portfolio-Rechner
        </h1>
        <p className="text-lg text-neutral-700 max-w-3xl mb-10">
          Wie stark unterscheidet sich der CO₂-Fußabdruck Ihres Depots, wenn Sie klassische
          Bausteine durch ESG-, Paris-Aligned- oder Impact-Produkte ersetzen? Dieser Rechner
          zeigt Ihnen die Größenordnungen – transparent und in verständlichen Einheiten.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-20" data-testid="calc-co2-tool">
        <CO2Calculator />
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-24">
        <div className="prose-editorial max-w-none">
          <h2>Warum ein CO₂-Fußabdruck für das Depot?</h2>
          <p>
            Der Kapitalmarkt ist kein neutraler Ort. Jeder investierte Euro finanziert reale
            Unternehmen – und damit indirekt auch deren Emissionsintensität. Der
            CO₂-Fußabdruck-Portfolio-Rechner übersetzt diese Verbindung in eine konkrete Kennzahl:
            Wie viele Tonnen CO₂-Äquivalente pro Jahr entfallen rechnerisch auf Ihr Depot? Und
            wie stark verändert sich diese Zahl, wenn Sie einen Teil klassischer Bausteine
            durch ESG-, Paris-Aligned- oder Impact-Produkte ersetzen?
          </p>

          <h2>Wie funktioniert die Rechenlogik?</h2>
          <p>
            Der Rechner nutzt vier Portfoliokategorien mit typischen durchschnittlichen
            Emissionsintensitäten pro 10.000 Euro Investitionsvolumen und Jahr. Klassische
            Weltmarkt-Indizes weisen historisch etwa 2.400 Kilogramm CO₂-Äquivalente pro
            10.000 Euro auf; ESG-Screened-Produkte kommen auf rund 1.300 Kilogramm; Paris-Aligned-
            Portfolios liegen bei etwa 700 Kilogramm; dedizierte Impact- und Cleantech-Fonds
            erreichen Werte um 350 Kilogramm. Der Rechner gewichtet Ihr eingegebenes Depotvolumen
            entsprechend Ihrer Anteilsverteilung und berechnet die Gesamtsumme.
          </p>

          <h2>Herkunft der Referenzwerte</h2>
          <p>
            Die verwendeten Werte stammen aus öffentlich verfügbaren Berichten großer
            Indexanbieter (MSCI, S&amp;P, Solactive), Zusammenfassungen der TCFD-konformen
            Berichterstattung von Fondsanbietern sowie aggregierten Marktstudien.
            Wichtig: Wir arbeiten bewusst mit Durchschnittswerten. Ihr tatsächlicher Fußabdruck
            hängt vom konkret gehaltenen Fonds ab; ESG-ETFs unterscheiden sich untereinander
            teils erheblich. Für eine exakte Analyse sind die im Jahresbericht des jeweiligen
            Fonds ausgewiesenen Kennzahlen (Scope-1- und Scope-2-Emissionen, CO₂-Intensität pro
            Umsatzmillion) heranzuziehen.
          </p>

          <h2>Interpretation: Was sagt die Zahl wirklich aus?</h2>
          <p>
            Die berechnete Emissionsintensität ist eine Momentaufnahme, kein moralisches Urteil.
            Sie erlaubt Vergleiche zwischen Depot-Konfigurationen, nicht aber absolute
            Aussagen über die Klimawirkung Ihres Investments. Ein Portfolio mit niedrigem
            CO₂-Fußabdruck finanziert Unternehmen, deren aktuelle Emissionen gering sind – das
            heißt nicht automatisch, dass dieses Kapital besonders viel zur Dekarbonisierung
            beiträgt. Transition- oder Engagement-Strategien können bewusst in emissionsstarke
            Unternehmen investieren, um deren Umbau zu begleiten. Beide Ansätze haben ihre
            Berechtigung und schließen sich nicht aus.
          </p>

          <h2>Grenzen der Berechnung</h2>
          <p>
            Der Rechner erfasst Scope 1 (direkte Emissionen) und Scope 2 (aus Energieverbrauch)
            – die vor- und nachgelagerten Scope-3-Emissionen sind mangels einheitlicher
            Datenlage nicht berücksichtigt. Gerade in Sektoren wie IT, Konsumgüter oder
            Finanzdienstleistungen liegt der Großteil der Emissionen jedoch in Scope 3. Die
            gezeigten Werte sind daher eine Untergrenze. Zudem berücksichtigen sie keine
            individuellen Fondsstrategien wie Ausschlusskriterien, Engagement-Aktivitäten oder
            SFDR-Klassifizierung. Für vertiefende Kontextualisierung empfehlen wir unseren
            Artikel {" "}<Link to="/artikel/esg-kriterien-einfach-erklaert">ESG-Kriterien einfach erklärt</Link>.
          </p>

          <h2>Warum absolute Zahlen mit Vorsicht zu genießen sind</h2>
          <p>
            Die berechnete Kilogrammzahl wirkt konkret – doch sie beruht auf Durchschnittswerten
            ganzer Kategorien. Zwei ESG-Screened-ETFs auf denselben Basisindex können sich in
            ihrer tatsächlichen Emissionsintensität um mehrere hundert Kilogramm CO₂e pro
            10.000 Euro unterscheiden, je nach Ausschlussschwellen, Regionenmix und
            Sektorgewichtung. Wer eine belastbare Zahl für ein reales Depot braucht, sollte
            zusätzlich in den jährlichen TCFD-Berichten der jeweiligen Emittenten nachschlagen
            oder ein spezialisiertes Analyse-Tool nutzen. Unser Rechner ist bewusst als
            didaktisches Modell konzipiert – er zeigt Muster und Größenordnungen, nicht die
            exakte Klimabilanz Ihres persönlichen Portfolios.
          </p>

          <h2>Typische Verzerrungen in CO₂-Bilanzen</h2>
          <p>
            Viele Investoren übersehen, dass CO₂-Bilanzen stark davon abhängen, wie
            Emissionen einem Unternehmen zugerechnet werden. Eine energieintensive
            Aluminiumhütte, die grünen Strom bezieht, weist möglicherweise niedrigere
            Scope-2-Emissionen aus als ein Softwarekonzern mit fossil versorgten
            Rechenzentren – obwohl die reale Klimawirkung eine andere ist. Auch die Wahl
            der Bezugsgröße spielt eine Rolle: Emissionen pro Umsatzmillion sehen bei
            hochpreisigen Nischenanbietern günstiger aus als bei margenschwachen
            Massenherstellern, obwohl das absolute Emissionsvolumen vergleichbar sein
            kann. Halten Sie sich diese Verzerrungen vor Augen, wenn Sie zwei Portfolios
            vergleichen: Die Kennzahl ist ein Anhaltspunkt, kein Verdikt.
          </p>

          <h2>Vom Modell zur echten Portfoliostrategie</h2>
          <p>
            Der CO₂-Fußabdruck-Rechner ist ein Einstieg – nicht das Ende der Analyse. Wer
            ernsthaft ein emissionsarmes Depot bauen möchte, kombiniert vier Ebenen:
            erstens die Auswahl der richtigen Anlagevehikel (Paris-Aligned, EU
            Climate Transition Benchmarks, dezidierte Cleantech-Fonds); zweitens die
            aktive Nutzung von Stimmrechten (Engagement-Voting statt bloßem Ausschluss);
            drittens die regelmäßige Überprüfung der Fondszusammensetzung anhand der
            veröffentlichten Emissionsdaten; und viertens die bewusste Entscheidung
            zwischen einer Ausschluss- und einer Transition-Strategie. Beide Wege können
            zu unterschiedlichen Fußabdruck-Ergebnissen führen – und trotzdem legitim
            sein. Nutzen Sie den Rechner, um diese strategischen Entscheidungen
            faktenbasiert zu treffen, nicht bauchgesteuert.
          </p>

          <h2>Wie Sie den Rechner sinnvoll nutzen</h2>
          <p>
            Beginnen Sie mit Ihrer aktuellen Portfolioaufstellung und geben Sie Ihre Anteile
            möglichst ehrlich ein – auch wenn 100 % klassische ETFs zunächst ernüchternde Werte
            liefern. Danach experimentieren Sie: Welche Wirkung hat es, wenn Sie 30 % Ihrer
            klassischen ETFs durch ESG-Screened-Varianten ersetzen? Wie stark ändert sich Ihr
            Fußabdruck, wenn Sie einen Impact-Fonds als Satellit hinzunehmen? Der Rechner
            liefert nicht die eine „richtige“ Antwort, sondern eine Diskussionsgrundlage für
            Ihre nächste Portfolioentscheidung – idealerweise ergänzt um die
            Renditeperspektive unseres <Link to="/rechner/nachhaltigkeits-rendite">Nachhaltigkeits-Rendite-Rechners</Link>{" "}
            und den Kostenvergleich im <Link to="/rechner/green-premium">Green Premium Rechner</Link>.
          </p>
          <p>
            Noch unsicher, welcher nachhaltige ETF überhaupt zu Ihnen passt? Unser{" "}
            <Link to="/rechner/nachhaltiger-etf-finder">ESG-Fit-Quiz</Link> filtert aus
            25 kuratierten ETFs die passende Empfehlung für Ihre Kriterien.
          </p>

          <h2>Fazit</h2>
          <p>
            Der CO₂-Fußabdruck-Portfolio-Rechner macht sichtbar, was in Prospekten oft nur in
            Fußnoten steht. Er ersetzt keine tiefgehende Portfolioanalyse, aber er liefert eine
            greifbare Kennzahl, mit der Sie Ihr Depot regelmäßig hinterfragen können. Nachhaltiges
            Investieren beginnt mit dem Wissen darüber, was das eigene Kapital heute finanziert.
          </p>
        </div>
      </section>
      <Footer />
    </div>
  );
}
